import { CalculatorState, CalculationResult } from "./types";

export const SOCIAL_COSTS_RATE = 0.3142;
export const FULL_VAT_RATE = 0.25;
export const DEDUCTIBLE_VAT_FACTOR = 0.5; // Only half VAT is part of the expense

/**
 * Calculates the monthly lease cost and the required salary deduction for cost neutrality.
 */
export const calculateDeduction = (state: CalculatorState): CalculationResult => {
  const { inputs, settings } = state;
  const { carPrice, residualValuePercent, marginalTaxPercent, benefitValue, workDrivingKm = 0, inputMode, leaseCostExclVat } = inputs;
  const { 
    annualInterestRate, 
    monthlyExtraCosts, 
    promotionDeduction, 
    taxFreeMileageAllowance = 2.5, 
    taxedMileageAllowance = 1.2,
    fuelCompensationRate = 1.2
  } = settings;

  let monthlyLeaseCost = 0;
  let leaseExpense = 0;
  let residualValue = 0;
  let principal = 0;
  let monthlyInterestOnRV = 0;
  let monthlyAnnuity = 0;

  if (inputMode === "DIRECT_LEASE") {
    // User enters lease cost excluding VAT
    // Gross cost for the result display
    monthlyLeaseCost = leaseCostExclVat * (1 + FULL_VAT_RATE);
    // Expense = Net + (VAT amount / 2)
    leaseExpense = leaseCostExclVat + (leaseCostExclVat * FULL_VAT_RATE) * DEDUCTIBLE_VAT_FACTOR;
  } else {
    const monthlyInterestRate = (annualInterestRate || 0) / 100 / 12;
    residualValue = carPrice * (residualValuePercent / 100);
    principal = carPrice - residualValue;
    const n = 36; // Always 36 months;

    // Monthly Interest on Residual Value
    monthlyInterestOnRV = residualValue * monthlyInterestRate;

    // Annuity calculation for the rest (principal)
    if (monthlyInterestRate === 0) {
      monthlyAnnuity = principal / n;
    } else {
      monthlyAnnuity =
        (principal * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, n)) /
        (Math.pow(1 + monthlyInterestRate, n) - 1);
    }

    monthlyLeaseCost = monthlyInterestOnRV + monthlyAnnuity;

    // Cost for the company
    // Lease includes full VAT, but only half VAT should be part of the expense.
    // Net price = Gross / (1 + Full VAT)
    // VAT amount = Gross - Net
    // Expense = Net + (VAT amount / 2)
    const netLease = monthlyLeaseCost / (1 + FULL_VAT_RATE);
    const vatAmount = monthlyLeaseCost - netLease;
    leaseExpense = netLease + vatAmount * DEDUCTIBLE_VAT_FACTOR;
  }

  const totalFuelCompensation = workDrivingKm * fuelCompensationRate;

  const totalCompanyCost =
    leaseExpense +
    monthlyExtraCosts +
    benefitValue * SOCIAL_COSTS_RATE +
    totalFuelCompensation;

  const workDrivingAllowance =
    workDrivingKm * taxFreeMileageAllowance +
    workDrivingKm * taxedMileageAllowance * (1 + SOCIAL_COSTS_RATE);

  // Income from salary deduction = Deduction * (1 + Social Costs) + Promotion + WorkDrivingAllowance
  // We need Income = Total Company Cost
  // Deduction * (1 + Social Costs) = Total Company Cost - Promotion - WorkDrivingAllowance
  // Deduction = (Total Company Cost - Promotion - WorkDrivingAllowance) / (1 + Social Costs)
  const grossSalaryDeduction = (totalCompanyCost - promotionDeduction - workDrivingAllowance) / (1 + SOCIAL_COSTS_RATE);

  // Refined Net Cost: (Gross Deduction * (1 - Marginal Tax)) + (Benefit Value * Marginal Tax)
  const netCostToEmployee = (grossSalaryDeduction * (1 - marginalTaxPercent / 100)) + (benefitValue * marginalTaxPercent / 100);

  const privateCarReferenceCompensation = workDrivingKm * (taxFreeMileageAllowance + taxedMileageAllowance * (1 - marginalTaxPercent / 100));

  return {
    monthlyLeaseCost,
    totalCompanyCost,
    grossSalaryDeduction,
    netCostToEmployee,
    totalFuelCompensation,
    privateCarReferenceCompensation,
    details: {
      leaseExpense,
      residualValue,
      principal,
      monthlyInterestOnRV,
      monthlyAnnuity,
      socialCostsOnBenefit: benefitValue * SOCIAL_COSTS_RATE,
      workDrivingAllowance,
    },
  };
};